import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink } from 'lucide-react';
import Button from '../ui/Button';
import inductionService from '../../services/induction';
import { errorMessage } from '../../utils/apiResponse';
import { useOrgTimezone } from '../../utils/timezone';
import type { InductionRegister, PolicyTemplate } from '../../types/induction';
import PackMatrix from './PackMatrix';

/**
 * The business induction pack: the WHS documents marked "part of the
 * induction pack", which everyone reads and acknowledges. The documents are
 * written and published on the WHS documents page; this panel explains that,
 * offers starter policies, and shows who has acknowledged what.
 */
export default function PolicyPackPanel() {
  const tz = useOrgTimezone();
  const [templates, setTemplates] = useState<PolicyTemplate[]>([]);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [register, setRegister] = useState<InductionRegister | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [installing, setInstalling] = useState(false);
  const [installed, setInstalled] = useState<{ id: number; title: string }[] | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [t, r] = await Promise.all([inductionService.policyTemplates(), inductionService.register()]);
      setTemplates([...t].sort((a, b) => a.order - b.order));
      setRegister(r);
    } catch (e) {
      setError(errorMessage(e, 'Could not load the induction pack'));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggle = (key: string) => setPicked(prev => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  const install = async () => {
    setInstalling(true);
    setError(null);
    try {
      setInstalled(await inductionService.installPolicyTemplates([...picked]) ?? []);
      setPicked(new Set());
    } catch (e) {
      setError(errorMessage(e, 'Could not add the policies'));
    } finally {
      setInstalling(false);
    }
  };

  return (
    <div className="space-y-6 text-sm">
      <section className="space-y-2 text-neutral-700">
        <h3 className="font-semibold text-neutral-900">The induction pack</h3>
        <p className="break-words">
          The pack is every WHS document marked <strong>part of the induction pack</strong> and published. Everyone it
          applies to is asked to read and acknowledge it in the app, and again whenever you publish a change that needs
          re-acknowledging. People without a login acknowledge on a supervisor’s phone, from their page in the register.
        </p>
        <Link to="/hr/whs/documents" className="inline-flex items-center gap-1 font-medium text-primary-700 hover:underline">
          <ExternalLink className="h-4 w-4 shrink-0" /> WHS documents
        </Link>
      </section>

      {error && <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 break-words">{error}</div>}

      <section className="space-y-3">
        <h3 className="font-semibold text-neutral-900">Add starter policies</h3>
        <p className="text-neutral-600 break-words">
          Plain-English starting points. They are added as <strong>drafts</strong> — read them, change them to suit how
          your farm actually works, and publish them from WHS documents. Nobody is asked to acknowledge a draft.
        </p>
        {loading && templates.length === 0 && <p className="text-neutral-500">Loading…</p>}
        {templates.length > 0 && (
          <ul className="divide-y divide-neutral-100 rounded-lg border border-neutral-200">
            {templates.map(t => (
              <li key={t.key}>
                <label className="flex items-start gap-2 p-3 cursor-pointer">
                  <input type="checkbox" className="mt-0.5 shrink-0" checked={picked.has(t.key)} onChange={() => toggle(t.key)} />
                  <span className="min-w-0 break-words text-neutral-800">{t.title}</span>
                </label>
              </li>
            ))}
          </ul>
        )}
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="secondary" onClick={() => setPicked(new Set(templates.map(t => t.key)))} disabled={!templates.length}>
            Select all
          </Button>
          <Button size="sm" onClick={install} isLoading={installing} disabled={picked.size === 0}>
            Add {picked.size || ''} as drafts
          </Button>
        </div>
        {installed && (
          <div className="rounded-lg border border-success-200 bg-success-50 p-3 text-success-800 break-words">
            {installed.length === 0
              ? 'Those policies were already added — they are on WHS documents.'
              : <>Added {installed.map(d => d.title).join(', ')} as drafts. Review and publish them on{' '}
                  <Link to="/hr/whs/documents" className="underline">WHS documents</Link>.</>}
          </div>
        )}
      </section>

      <section className="space-y-3">
        <h3 className="font-semibold text-neutral-900">Who has acknowledged</h3>
        {register && <PackMatrix register={register} people={register.people} tz={tz} />}
      </section>
    </div>
  );
}
